import { call, put, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import { Notification } from '../../../components/ui/custom-notification';
import { pipeMillReportApi } from './api';
import {
  FETCH_PIPE_MILL_REPORTS,
  FETCH_PIPE_MILL_MOCK_DATA,
  FETCH_PIPE_MILL_REPORT_METADATA,
  SAVE_PIPE_MILL_REPORT,
  SAVE_BULK_PIPE_MILL_REPORTS,
  SAVE_PIPE_MILL_REPORT_ITEM,
  SAVE_PIPE_MILL_REPORT_STATUS,
  DELETE_PIPE_MILL_REPORT,
  DELETE_PIPE_MILL_REPORT_ITEM,
  EVALUATE_PIPE_MILL_REPORT,
  FETCH_PIPE_MILL_PROJECT_LIMITS,
  SAVE_PIPE_MILL_PROJECT_LIMIT,
  DELETE_PIPE_MILL_PROJECT_LIMIT,
  FETCH_PIPE_MILL_FREQUENCY_RULES,
  SAVE_PIPE_MILL_FREQUENCY_RULE,
  DELETE_PIPE_MILL_FREQUENCY_RULE,
  FETCH_PIPE_MILL_CLEARANCE_STATUS,
  FETCH_PIPE_MILL_REPORT_METADATA_SUCCESS,
  FETCH_PIPE_MILL_REPORT_METADATA_FAILURE,
} from './types';
import {
  fetchPipeMillReports,
  fetchPipeMillReportsSuccess,
  fetchPipeMillReportsFailure,
  fetchPipeMillMockDataSuccess,
  fetchPipeMillMockDataFailure,
  savePipeMillReportSuccess,
  savePipeMillReportFailure,
  saveBulkPipeMillReportsSuccess,
  saveBulkPipeMillReportsFailure,
  savePipeMillReportItemSuccess,
  savePipeMillReportItemFailure,
  savePipeMillReportStatusSuccess,
  savePipeMillReportStatusFailure,
  deletePipeMillReportSuccess,
  deletePipeMillReportFailure,
  deletePipeMillReportItemSuccess,
  deletePipeMillReportItemFailure,
  evaluatePipeMillReportSuccess,
  evaluatePipeMillReportFailure,
  fetchPipeMillProjectLimits,
  fetchPipeMillProjectLimitsSuccess,
  fetchPipeMillProjectLimitsFailure,
  savePipeMillProjectLimitSuccess,
  savePipeMillProjectLimitFailure,
  deletePipeMillProjectLimitSuccess,
  deletePipeMillProjectLimitFailure,
  fetchPipeMillFrequencyRules,
  fetchPipeMillFrequencyRulesSuccess,
  fetchPipeMillFrequencyRulesFailure,
  savePipeMillFrequencyRuleSuccess,
  savePipeMillFrequencyRuleFailure,
  deletePipeMillFrequencyRuleSuccess,
  deletePipeMillFrequencyRuleFailure,
  fetchPipeMillClearanceStatusSuccess,
  fetchPipeMillClearanceStatusFailure,
} from './actions';

const errorMessage = (error, fallback) =>
  get(error, 'response.data.message') ||
  get(error, 'response.data.data') ||
  get(error, 'message') ||
  fallback;

function* fetchReportsSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.fetchReports, payload);
    yield put(fetchPipeMillReportsSuccess(Array.isArray(data) ? data : []));
  } catch (error) {
    yield put(fetchPipeMillReportsFailure(error));
  }
}

function* fetchMockDataSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.fetchMockData, payload);
    yield put(fetchPipeMillMockDataSuccess(data || {}));
  } catch (error) {
    yield put(fetchPipeMillMockDataFailure(error));
  }
}

function* fetchMetadataSaga({ payload }) {
  const reportType = payload?.reportType;
  try {
    const data = yield call(pipeMillReportApi.fetchMetadata, payload);
    yield put({
      type: FETCH_PIPE_MILL_REPORT_METADATA_SUCCESS,
      payload: data,
      meta: { reportType },
    });
  } catch (error) {
    yield put({
      type: FETCH_PIPE_MILL_REPORT_METADATA_FAILURE,
      error,
      meta: { reportType },
    });
  }
}

function* saveReportSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveReport, payload);
    yield put(savePipeMillReportSuccess(data));
    yield put(fetchPipeMillReports({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      pipeTraceabilityId: payload?.pipeTraceabilityId,
      itemNumber: payload?.itemNumber,
      heatNumber: payload?.heatNumber,
      pipeNumber: payload?.pipeNumber,
      reportType: payload?.reportType,
    }));
    yield put({
      type: FETCH_PIPE_MILL_REPORT_METADATA,
      payload: {
        projectId: payload?.projectId,
        pipeId: payload?.pipeId,
        heatNumber: payload?.heatNumber,
        pipeNumber: payload?.pipeNumber,
      },
    });
    Notification.success('Pipe mill report metadata saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save pipe mill report metadata');
    yield put(savePipeMillReportFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* saveBulkReportsSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveBulkReports, payload);
    yield put(saveBulkPipeMillReportsSuccess(data));
    yield put(fetchPipeMillReports({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      heatNumber: payload?.heatNumber,
    }));
    yield put({
      type: FETCH_PIPE_MILL_REPORT_METADATA,
      payload: {
        projectId: payload?.projectId,
        pipeId: payload?.pipeId,
        heatNumber: payload?.heatNumber,
      },
    });
    Notification.success('Pipe mill reports saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save pipe mill reports');
    yield put(saveBulkPipeMillReportsFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* saveReportItemSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveReportItem, payload);
    yield put(savePipeMillReportItemSuccess(data));
    yield put(fetchPipeMillReports({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      heatNumber: payload?.heatNumber,
    }));
    Notification.success('Pipe mill report item saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save pipe mill report item');
    yield put(savePipeMillReportItemFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* saveReportStatusSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveReportStatus, payload);
    yield put(savePipeMillReportStatusSuccess(data));
    yield put(fetchPipeMillReports({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      heatNumber: payload?.heatNumber,
      pipeNumber: payload?.pipeNumber,
    }));
    Notification.success('Report adjudication saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save report adjudication');
    yield put(savePipeMillReportStatusFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* deleteReportSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.deleteReport, payload);
    yield put(deletePipeMillReportSuccess(data || payload));
    Notification.success('Pipe mill report deleted');
  } catch (error) {
    const message = errorMessage(error, 'Failed to delete pipe mill report');
    yield put(deletePipeMillReportFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* deleteReportItemSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.deleteReportItem, payload);
    yield put(deletePipeMillReportItemSuccess(data || payload));
    Notification.success('Pipe mill report item deleted');
  } catch (error) {
    const message = errorMessage(error, 'Failed to delete pipe mill report item');
    yield put(deletePipeMillReportItemFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* evaluateReportSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.evaluateReport, payload);
    yield put(evaluatePipeMillReportSuccess(data));
    Notification.success('Pipe mill report evaluated');
  } catch (error) {
    const message = errorMessage(error, 'Failed to evaluate pipe mill report');
    yield put(evaluatePipeMillReportFailure(error));
    Notification.error('Pipe mill report', String(message));
  }
}

function* fetchProjectLimitsSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.fetchProjectLimits, payload);
    yield put(fetchPipeMillProjectLimitsSuccess(Array.isArray(data) ? data : []));
  } catch (error) {
    yield put(fetchPipeMillProjectLimitsFailure(error));
  }
}

function* saveProjectLimitSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveProjectLimit, payload);
    yield put(savePipeMillProjectLimitSuccess(data));
    yield put(fetchPipeMillProjectLimits({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      reportType: payload?.reportType,
      itemNumber: payload?.itemNumber,
    }));
    Notification.success('Project limit saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save project limit');
    yield put(savePipeMillProjectLimitFailure(error));
    Notification.error('Project limits', String(message));
  }
}

function* deleteProjectLimitSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.deleteProjectLimit, payload);
    yield put(deletePipeMillProjectLimitSuccess(data || payload));
    Notification.success('Project limit deleted');
  } catch (error) {
    const message = errorMessage(error, 'Failed to delete project limit');
    yield put(deletePipeMillProjectLimitFailure(error));
    Notification.error('Project limits', String(message));
  }
}

function* fetchFrequencyRulesSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.fetchFrequencyRules, payload);
    yield put(fetchPipeMillFrequencyRulesSuccess(Array.isArray(data) ? data : []));
  } catch (error) {
    yield put(fetchPipeMillFrequencyRulesFailure(error));
  }
}

function* saveFrequencyRuleSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.saveFrequencyRule, payload);
    yield put(savePipeMillFrequencyRuleSuccess(data));
    yield put(fetchPipeMillFrequencyRules({
      projectId: payload?.projectId,
      pipeId: payload?.pipeId,
      projectLimitId: payload?.projectLimitId || payload?.project_limit_id,
      reportType: payload?.reportType,
      itemNumber: payload?.itemNumber,
    }));
    Notification.success('Frequency rule saved');
  } catch (error) {
    const message = errorMessage(error, 'Failed to save frequency rule');
    yield put(savePipeMillFrequencyRuleFailure(error));
    Notification.error('Frequency rules', String(message));
  }
}

function* deleteFrequencyRuleSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.deleteFrequencyRule, payload);
    yield put(deletePipeMillFrequencyRuleSuccess(data || payload));
    Notification.success('Frequency rule deleted');
  } catch (error) {
    const message = errorMessage(error, 'Failed to delete frequency rule');
    yield put(deletePipeMillFrequencyRuleFailure(error));
    Notification.error('Frequency rules', String(message));
  }
}

function* fetchClearanceStatusSaga({ payload }) {
  try {
    const data = yield call(pipeMillReportApi.fetchClearanceStatus, payload);
    yield put(fetchPipeMillClearanceStatusSuccess(data));
  } catch (error) {
    yield put(fetchPipeMillClearanceStatusFailure(error));
  }
}

export default function* pipeMillReportSaga() {
  yield takeLatest(FETCH_PIPE_MILL_REPORTS, fetchReportsSaga);
  yield takeLatest(FETCH_PIPE_MILL_MOCK_DATA, fetchMockDataSaga);
  yield takeLatest(FETCH_PIPE_MILL_REPORT_METADATA, fetchMetadataSaga);
  yield takeLatest(SAVE_PIPE_MILL_REPORT, saveReportSaga);
  yield takeLatest(SAVE_BULK_PIPE_MILL_REPORTS, saveBulkReportsSaga);
  yield takeLatest(SAVE_PIPE_MILL_REPORT_ITEM, saveReportItemSaga);
  yield takeLatest(SAVE_PIPE_MILL_REPORT_STATUS, saveReportStatusSaga);
  yield takeLatest(DELETE_PIPE_MILL_REPORT, deleteReportSaga);
  yield takeLatest(DELETE_PIPE_MILL_REPORT_ITEM, deleteReportItemSaga);
  yield takeLatest(EVALUATE_PIPE_MILL_REPORT, evaluateReportSaga);
  yield takeLatest(FETCH_PIPE_MILL_PROJECT_LIMITS, fetchProjectLimitsSaga);
  yield takeLatest(SAVE_PIPE_MILL_PROJECT_LIMIT, saveProjectLimitSaga);
  yield takeLatest(DELETE_PIPE_MILL_PROJECT_LIMIT, deleteProjectLimitSaga);
  yield takeLatest(FETCH_PIPE_MILL_FREQUENCY_RULES, fetchFrequencyRulesSaga);
  yield takeLatest(SAVE_PIPE_MILL_FREQUENCY_RULE, saveFrequencyRuleSaga);
  yield takeLatest(DELETE_PIPE_MILL_FREQUENCY_RULE, deleteFrequencyRuleSaga);
  yield takeLatest(FETCH_PIPE_MILL_CLEARANCE_STATUS, fetchClearanceStatusSaga);
}
